import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

/**
 * The `linkComponent` Polaris routes every link through.
 *
 * ── The bug this exists for ────────────────────────────────────────────────
 *
 * Clicking "Shops" showed the Dashboard. Not sometimes — always, for every nav
 * item except the one already selected.
 *
 * `Navigation.Item` is given `url: 'shops'`, and Polaris renders it as a real
 * anchor. Its own click handler only cancels the navigation when the href
 * matches the `location` prop:
 *
 *     if (currentTarget.getAttribute('href') === location) event.preventDefault();
 *
 * We pass `location={route}`, so that is true only for the CURRENTLY SELECTED
 * item. Clicking any other one ran our `onClick` — setting the route correctly —
 * and then let the browser navigate to `/shops` anyway. The ops Worker serves
 * `not_found_handling: single-page-application`, so `/shops` returns the shell
 * with 200, the app boots from scratch, and `useState<Route>('dashboard')`
 * resets. The state change was real and instantly thrown away.
 *
 * ⚠ Why not simply drop `url`. An anchor with no href is not in the tab order,
 * so that fix trades a mouse bug for a keyboard one and silently makes the whole
 * ops navigation unreachable without a pointer.
 *
 * So: keep the href — hover targets and keyboard focus both need it — and
 * cancel the navigation here. Polaris passes its own handler through as
 * `onClick`, and that handler is what calls `onNavigate`, so routing still
 * happens. This is Polaris's documented pattern for client-side routing, and
 * fixing it at the provider covers every link in the app rather than the six
 * in the nav.
 */
export function OpsLink({
  url = '',
  external,
  children,
  onClick,
  ...rest
}: {
  url?: string;
  external?: boolean;
  children?: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  /**
   * ⚠ An external link must still navigate. Cancelling those would turn every
   * outbound link — a merchant's storefront, a Shopify admin deep link — into a
   * dead element, which is a worse bug than the one above and harder to notice.
   */
  const isExternal = external === true || /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//');

  if (isExternal) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <a
      href={url}
      {...rest}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </a>
  );
}
